import React, { useEffect, useState, useMemo } from "react";
import {
  DataGridPro,
  GridRowParams,
  GridValueGetterParams,
  useGridApiRef,
  GridColDef,
} from "@mui/x-data-grid-pro";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReloadButton from "../common/ReloadButton";
import { getNotices, INoticesRow } from "../../webapi/service/getNotices";
import { setNoticesRead } from "../../webapi/service/setNoticesRead";
import { getProjects } from "../../webapi/service/getProjects";
import AuthService from "../../common/AuthService";
import "../main.css";
import "./notice.css";

interface SelectNoticeParam {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * 選択した通知
   */
  notice: INoticesRow;
  /**
   * 通知のプロジェクトが存在する場合に呼び出します
   */
  onNotice: (data: INoticesRow) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   * @param onCloseMessage - メッセージBoxを閉じる場合に呼び出します
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined,
    onCloseMessage?: () => void
  ) => void;
  /**
   * プロジェクト取得エラーメッセージBOXを閉じる場合に呼び出します
   */
  onCloseMessage?: () => void;
}

export function selectNotice(param: SelectNoticeParam) {
  const { authService, notice, onNotice, onCommunicationError } = param;
  setNoticesRead({
    authService: authService,
    noticeids: [notice.noticeid],
    success: () => {
      const words = notice.uri.split("/");
      const projectid = words[2];
      getProjects({
        authService: authService,
        projectid: projectid,
        success: (rows) => {
          if (rows.length > 0) {
            onNotice(notice);
          }
        },
        fail: (id, status) => {
          onCommunicationError(id, status, param.onCloseMessage);
        },
      });
    },
    fail: (id, status) => {
      onCommunicationError(id, status);
    },
  });
}

export interface IProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * 通知を選択した場合に呼び出します
   * @param data - 通知データ
   */
  onNotice: (data: INoticesRow) => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   * @param onCloseMessage - メッセージBoxを閉じる場合に呼び出します
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined,
    onCloseMessage?: () => void
  ) => void;
  /**
   * 非操作タイムアウトをリセットします
   */
  resetUnoperatedTimeout: () => void;
}

export default function NoticeMainSpace(props: IProps) {
  const {
    authService,
    onNotice,
    onLoading,
    onCommunicationError,
    resetUnoperatedTimeout,
  } = props;
  const [rows, setRows] = useState<INoticesRow[]>([]);
  const [buttonVisible, setButtonVisible] = useState<boolean>(false);
  const { i18n, t } = useTranslation();
  const apiRef = useGridApiRef();

  useEffect(() => {
    loadNotices(true);
  }, []);

  function clearCheckRows() {
    apiRef.current.selectRows(apiRef.current.getAllRowIds(), false);
    setButtonVisible(false);
  }

  function loadNotices(loading: boolean) {
    onLoading(loading);
    clearCheckRows();
    getNotices({
      authService: authService,
      lang: i18n.language,
      success: (rows) => {
        setRows(rows);
        onLoading(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function handleClickSetUnread() {
    onLoading(true);
    resetUnoperatedTimeout();
    const noticeids: string[] = [];
    const map = apiRef.current.getSelectedRows();
    map.forEach((value) => {
      noticeids.push(value.noticeid);
    });
    if (noticeids.length > 0) {
      setNoticesRead({
        authService: authService,
        noticeids: noticeids,
        success: () => {
          loadNotices(false);
        },
        fail: (id, status) => {
          onCommunicationError(id, status);
        },
      });
    }
  }

  function handleReload() {
    resetUnoperatedTimeout();
    loadNotices(true);
  }

  function onSelectNotice(noticeRow: INoticesRow) {
    selectNotice({
      authService: authService,
      notice: noticeRow,
      onNotice: onNotice,
      onCommunicationError: onCommunicationError,
      onCloseMessage: handleReload,
    });
  }

  function getRow(id: number): INoticesRow | undefined {
    return rows.find((row) => {
      return row.id === id;
    });
  }

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "noticeid",
        headerName: t("notifications_detail_noticeid"),
        width: 100,
        hide: true,
      },
      {
        field: "noticetime",
        headerName: t("notifications_detail_noticetime"),
        width: 200,
        sortable: true,
      },
      {
        field: "unread",
        headerName: t("notifications_detail_status"),
        width: 120,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => {
          const { unread } = params.row;
          return unread === true
            ? t("notifications_detail_unread")
            : t("notifications_detail_read");
        },
      },
      {
        field: "level",
        headerName: t("notifications_detail_level"),
        width: 100,
        hide: true,
      },
      {
        field: "message",
        headerName: t("notifications_detail_message"),
        width: 600,
        sortable: true,
      },
      {
        field: "projectname",
        headerName: t("notifications_detail_projectname"),
        width: 200,
        sortable: true,
      },
      {
        field: "uri",
        headerName: t("notifications_detail_uri"),
        width: 100,
        hide: true,
      },
    ];
  }, [i18n.language]);

  const unreadBtn = buttonVisible ? (
    <Button className="btn-mainspace" onClick={handleClickSetUnread}>
      {t("notifications_already_read")}
    </Button>
  ) : (
    ""
  );

  const dataGrid = useMemo(() => {
    return (
      <DataGridPro
        columns={columns}
        rows={rows}
        disableSelectionOnClick
        disableColumnMenu
        checkboxSelection
        isRowSelectable={(params: GridRowParams) => params.row.unread === true}
        onSelectionModelChange={(selecttionModel) => {
          setButtonVisible(selecttionModel.length > 0 ? true : false);
        }}
        onCellClick={(params) => {
          if (params.field === "__check__") {
            return;
          }
          if (params.field === "actions") {
            return;
          }
          const rowData = getRow(params.row.id);
          if (rowData) {
            onSelectNotice(rowData);
          }
        }}
        getRowClassName={() => {
          return "grid-row";
        }}
        apiRef={apiRef}
        localeText={{
          noRowsLabel: "",
          footerTotalRows: t("notifications_detail_total_rows") + " : ",
          footerRowSelected: (count) => t("list_total_selecedtitem") + " : " + count,
        }}
      />
    );
  }, [rows, i18n.language]);

  return (
    <div className="mainspace">
      <div className="mainspace-title">
        <h3>{t("notifications_all")}</h3>
      </div>
      <div className="mainspace-toolbar">
        <ReloadButton onClick={handleReload} />
        {unreadBtn}
      </div>
      <div className="notice-grid">{dataGrid}</div>
    </div>
  );
}
